import React, { useState, useRef, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Input from './ui/Input';
import { cn } from '../utils/cn';
import { streamChatCompletion } from '../services/openaiService';
import { formatSystemContextForAI } from '../services/dataContextService';
import { trackAIChatInteraction } from '../services/analyticsService';

const AIChatOverlay = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m your AI assistant with access to your business analytics data. I can help you analyze metrics, identify trends, and provide insights from your executive dashboard. What would you like to know about your business performance?',
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isOpen]);

  const handleClearConversation = () => {
    setMessages([
      {
        role: 'assistant',
        content: 'Conversation cleared. How can I help you with your business analytics?',
      },
    ]);
    trackAIChatInteraction('conversation_cleared', {
      messages_cleared: messages?.length,
    });
  };

  const handleSendMessage = async () => {
    if (!inputValue?.trim() || isLoading) return;

    const userMessage = inputValue?.trim();
    setInputValue('');
    setIsLoading(true);

    // Track AI chat message sent
    trackAIChatInteraction('message_sent', {
      message_length: userMessage?.length,
      conversation_length: messages?.length,
    });

    // Add user message
    const newMessages = [
      ...messages,
      { role: 'user', content: userMessage },
    ];
    setMessages(newMessages);

    // Add empty assistant message for streaming
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: '' },
    ]);

    // Get system data context
    const systemContext = formatSystemContextForAI();

    // Limit conversation history to last 10 messages (5 exchanges) to reduce token usage
    const recentMessages = newMessages?.slice(-10)?.filter(m => m?.role !== 'system');

    // Stream response with system context
    let assistantResponse = '';
    await streamChatCompletion(
      recentMessages,
      (chunk) => {
        assistantResponse += chunk;
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: assistantResponse,
          };
          return updated;
        });
      },
      (error) => {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'assistant',
            content: `Error: ${error}`,
          };
          return updated;
        });
      },
      systemContext // Pass system context
    );

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    
    // Track AI chat open/close
    trackAIChatInteraction(newState ? 'chat_opened' : 'chat_closed', {
      messages_count: messages?.length,
    });
  };

  return (
    <>
      {/* Floating AI Button */}
      <button
        onClick={handleToggle}
        className={cn(
          'fixed bottom-6 right-6 z-[110] flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          isOpen
            ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            : 'bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-110'
        )}
        aria-label={isOpen ? 'Close AI Chat' : 'Open AI Chat'}
      >
        <Icon name={isOpen ? 'X' : 'MessageCircle'} size={24} />
      </button>
      {/* Backdrop Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-background/80 z-[105] lg:hidden"
          onClick={handleToggle}
        />
      )}
      {/* Chat Panel */}
      <div
        className={cn(
          'fixed bottom-24 right-6 w-[calc(100vw-3rem)] sm:w-96 h-[32rem] bg-card border border-border rounded-lg shadow-2xl z-[110] flex flex-col transition-all duration-300',
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50 rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10">
              <Icon name="Bot" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                AI Assistant
              </h3>
              <p className="text-xs text-muted-foreground">
                Ask me anything about your data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {messages?.length > 1 && (
              <button
                onClick={handleClearConversation}
                className="p-2 rounded-md hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Clear conversation"
                title="Clear conversation"
              >
                <Icon name="Trash2" size={16} />
              </button>
            )}
            <button
              onClick={handleToggle}
              className="p-2 rounded-md hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Close chat"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages?.map((message, index) => (
            <div
              key={index}
              className={cn(
                'flex gap-3',
                message?.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              {message?.role === 'assistant' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="Bot" size={16} color="var(--color-primary)" />
                </div>
              )}
              <div
                className={cn(
                  'max-w-[80%] rounded-lg px-4 py-2 text-sm',
                  message?.role === 'user' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground'
                )}
              >
                {message?.content}
              </div>
              {message?.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                  <Icon name="User" size={16} color="var(--color-secondary)" />
                </div>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Bot" size={16} color="var(--color-primary)" />
              </div>
              <div className="bg-muted rounded-lg px-4 py-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border bg-muted/50 rounded-b-lg">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about your data..."
              disabled={isLoading}
              className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue?.trim() || isLoading}
              size="icon"
              iconName="Send"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AIChatOverlay;